/**
 * Cash-on-delivery checkout modal.
 *
 * Intercepts the cart drawer's Checkout button and the product page's Buy it
 * now button, and opens a modal instead of handing off to Shopify checkout.
 * On submit it posts the cart to the COD worker, which creates an unpaid order.
 *
 * Only variant ids and quantities are sent. Prices shown here are for the
 * customer's benefit; the real total is computed by Shopify server-side.
 */

class CodCheckout {
  constructor(root) {
    this.root = root;
    // The config script is a sibling of the modal, not a child of it.
    this.config = JSON.parse(document.querySelector('[data-cod-config]').textContent);
    this.form = root.querySelector('[data-cod-form]');
    this.itemsEl = root.querySelector('[data-cod-items]');
    this.totalsEl = root.querySelector('[data-cod-totals]');
    this.errorEl = root.querySelector('[data-cod-error]');
    this.submitEl = root.querySelector('[data-cod-submit]');
    this.bodyEl = root.querySelector('[data-cod-body]');
    this.doneEl = root.querySelector('[data-cod-done]');
    this.cart = null;
    /** @type {HTMLElement | null} Where focus goes back to when the modal closes. */
    this.previouslyFocused = null;

    this.bind();
  }

  bind() {
    // Capture phase: Shopify's own handlers are on the buttons themselves, so
    // we have to win the race before they navigate to /checkout.
    document.addEventListener('click', (event) => this.onClick(event), true);

    this.root.addEventListener('click', (event) => {
      if (event.target === this.root || event.target.closest('[data-cod-close]')) this.close();
    });

    // A modal dialog closes itself on Escape, but not every engine routes a
    // key event to the close watcher, so keep the explicit handler this modal
    // has always had. Closing twice is a no-op.
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });

    // Every close path — the ×, the backdrop, Escape — ends in the dialog's
    // own `close` event, so cleanup lives there and runs once.
    this.root.addEventListener('close', () => this.onClosed());

    this.form.addEventListener('submit', (event) => this.onSubmit(event));

    this.itemsEl.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cod-remove]');
      if (remove) this.removeLine(remove.dataset.codRemove);
    });
  }

  get isOpen() {
    return this.root.open;
  }

  async onClick(event) {
    const checkout = event.target.closest('button[name="checkout"], [data-cod-open]');
    const buyNow = event.target.closest('.shopify-payment-button__button');
    if (!checkout && !buyNow) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Buy it now skips the cart, so put the chosen variant in it first —
    // that keeps the modal a single, consistent view of what's being ordered.
    if (buyNow) {
      const form = buyNow.closest('form[data-type="add-to-cart-form"]');
      const id = form?.querySelector('[name="id"]')?.value;
      if (id) {
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
        });
      }
    }

    this.open();
  }

  async open() {
    // The drawer this was opened from is a dialog too, and it fights us for
    // focus: modal-mode drawers inert everything outside themselves, and
    // sidebar-mode drawers run a focus trap that pulls focus back on focusin.
    // Close it — the modal already shows the same lines, with the same
    // remove buttons.
    this.closeDrawers();

    if (!this.isOpen) {
      this.previouslyFocused =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // showModal(), not an [open] attribute: only the top layer sits above
      // another open dialog, and only a modal dialog traps focus natively.
      this.root.showModal();
    }

    document.body.style.overflow = 'hidden';
    this.bodyEl.hidden = false;
    this.doneEl.hidden = true;
    this.focusFirstField();
    await this.refresh();
  }

  /**
   * Every drawer in the theme is a `<theme-drawer>`, whose close() tears down
   * its focus trap. Skipped if the custom element hasn't upgraded yet, which
   * is harmless: showModal() has already made it inert.
   */
  closeDrawers() {
    for (const drawer of document.querySelectorAll('theme-drawer[open]')) {
      drawer.close?.();
    }
  }

  /** Put the caret in the form directly — clicking in first is the bug this fixes. */
  focusFirstField() {
    this.form.querySelector('input:not([type="hidden"]):not(.cod-honeypot)')?.focus();
  }

  close() {
    if (this.isOpen) this.root.close();
  }

  /** Runs on the dialog's own `close` event, whichever path got us there. */
  onClosed() {
    document.body.style.overflow = '';
    const trigger = this.previouslyFocused;
    this.previouslyFocused = null;
    // A cart re-render can replace the button we came from; focus() on a
    // detached node is a silent no-op, so only refocus what's still in the DOM.
    if (trigger && document.contains(trigger)) trigger.focus();
  }

  money(cents) {
    const amount = (cents / 100).toLocaleString(this.config.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${amount} ${this.config.currencySuffix}`;
  }

  async refresh() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    this.cart = await res.json();
    this.render();
  }

  render() {
    const { items } = this.cart;

    if (!items.length) {
      this.itemsEl.innerHTML = `<li class="cod-item"><span>${this.config.strings.empty}</span></li>`;
      this.totalsEl.innerHTML = '';
      this.submitEl.disabled = true;
      this.submitEl.querySelector('[data-cod-total]').textContent = '';
      return;
    }

    this.submitEl.disabled = false;

    this.itemsEl.innerHTML = items
      .map(
        (item) => `
        <li class="cod-item">
          <span class="cod-item__media">
            <img src="${item.image ? `${item.image}&width=140` : ''}" alt="" loading="lazy" width="56" height="56">
            <span class="cod-item__qty">${item.quantity}</span>
          </span>
          <span>
            <span class="cod-item__title">${this.escape(item.product_title)}</span>
            ${item.variant_title ? `<span class="cod-item__variant">${this.escape(item.variant_title)}</span>` : ''}
          </span>
          <span class="cod-item__price">${this.money(item.final_line_price)}</span>
          <button type="button" class="cod-item__remove" data-cod-remove="${item.key}"
                  aria-label="${this.escape(this.config.strings.remove)}">&times;</button>
        </li>`,
      )
      .join('');

    const discount = this.cart.total_discount;
    const shipping = this.shippingCost();

    this.totalsEl.innerHTML = `
      <div class="cod-totals__row">
        <span>${this.config.strings.subtotal}</span>
        <span>${this.money(this.cart.items_subtotal_price)}</span>
      </div>
      <div class="cod-totals__row">
        <span>${this.config.strings.shipping}</span>
        <span>${this.shippingValue(shipping)}</span>
      </div>
      ${
        discount > 0
          ? `<div class="cod-totals__row cod-totals__discount">
               <span>${this.config.strings.discounts}</span>
               <span>- ${this.money(discount)}</span>
             </div>`
          : ''
      }
      <div class="cod-totals__row cod-totals__row--grand">
        <span>${this.config.strings.total}</span>
        <span>${this.money(this.cart.total_price + shipping)}</span>
      </div>`;

    this.submitEl.querySelector('[data-cod-total]').textContent = this.money(
      this.cart.total_price + shipping,
    );
  }

  /**
   * Display only, and it does not reach Shopify. The order is created from
   * line items alone, so its total is the goods; the supplier handles delivery,
   * and the courier collects this on top when the cart is under the threshold.
   */
  shippingCost() {
    const { freeShippingThreshold, shippingFee } = this.config;
    if (!shippingFee) return 0;
    return this.cart.items_subtotal_price >= freeShippingThreshold ? 0 : shippingFee;
  }

  /**
   * The right-hand side of the Shipping row. Under the threshold it's the fee;
   * at or above it the fee is struck through next to "Free", so the customer
   * sees what crossing 400 just saved them. A fee of 0 means delivery is free
   * unconditionally and there is nothing to strike out.
   */
  shippingValue(shipping) {
    const free = `<span class="cod-totals__free">${this.config.strings.free}</span>`;
    if (shipping > 0) return this.money(shipping);
    if (!this.config.shippingFee) return free;
    return `<s class="cod-totals__struck">${this.money(this.config.shippingFee)}</s> ${free}`;
  }

  escape(value) {
    const el = document.createElement('div');
    el.textContent = value ?? '';
    return el.innerHTML;
  }

  async removeLine(key) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    await this.refresh();
    document.dispatchEvent(new CustomEvent('cod:cart-updated'));
  }

  showFieldErrors(fields) {
    this.form.querySelectorAll('.cod-field').forEach((f) => f.classList.remove('is-invalid'));
    for (const field of fields) {
      this.form.querySelector(`[name="${field}"]`)?.closest('.cod-field')?.classList.add('is-invalid');
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    this.errorEl.hidden = true;

    const data = Object.fromEntries(new FormData(this.form));
    const payload = {
      // `items` is what a real order is built from — ids and quantities only,
      // priced server-side. `lines` and `total` are human-readable copies for
      // the spreadsheet, and are only as trustworthy as the browser that sent
      // them; never price anything from these.
      items: this.cart.items.map((i) => ({ variantId: i.variant_id, quantity: i.quantity })),
      lines: this.cart.items.map((i) => ({
        title: i.product_title,
        variant: i.variant_title || '',
        quantity: i.quantity,
        price: i.final_line_price / 100,
      })),
      total: (this.cart.total_price + this.shippingCost()) / 100,
      customer: {
        name: data.name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
      },
      company: data.company, // honeypot
    };

    this.submitEl.disabled = true;
    const label = this.submitEl.querySelector('[data-cod-label]');
    const original = label.textContent;
    label.textContent = this.config.strings.sending;

    try {
      // text/plain keeps this a "simple" request, so the browser skips the
      // CORS preflight. Google Apps Script can't answer an OPTIONS preflight,
      // and the Cloudflare Worker parses the body regardless of content type.
      const res = await fetch(this.config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok || !body.ok) {
        if (res.status === 422 && body.fields) this.showFieldErrors(body.fields);
        throw new Error(body.error || 'failed');
      }

      await fetch('/cart/clear.js', { method: 'POST' });
      this.root.querySelector('[data-cod-ref]').textContent = body.ref ?? '';
      this.bodyEl.hidden = true;
      this.doneEl.hidden = false;
    } catch {
      this.errorEl.hidden = false;
    } finally {
      this.submitEl.disabled = false;
      label.textContent = original;
    }
  }
}

function initCodCheckout() {
  const root = document.querySelector('[data-cod-modal]');
  // Guard against double-binding: Horizon re-renders sections in the theme
  // editor, and this file may be evaluated after the document is already done.
  if (root && !root.dataset.codReady) {
    root.dataset.codReady = 'true';
    new CodCheckout(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCodCheckout);
} else {
  initCodCheckout();
}

document.addEventListener('shopify:section:load', initCodCheckout);
